import "../styles/globals.css";
import type { AppProps } from "next/app";
import Head from "next/head";
import Script from "next/script";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { IntlProvider, useTranslations } from "next-intl";

import enMessages from "../messages/en.json";
import zhMessages from "../messages/zh.json";
import jaMessages from "../messages/ja.json";
import koMessages from "../messages/ko.json";
import thMessages from "../messages/th.json";
import viMessages from "../messages/vi.json";
import idMessages from "../messages/id.json";
import msMessages from "../messages/ms.json";
import esMessages from "../messages/es.json";

type SupportedLocale = "en" | "zh" | "ja" | "ko" | "th" | "vi" | "id" | "ms" | "es";

const LANG_STORAGE_KEY = "playsout.lang";

const normalizeLocale = (value?: string): SupportedLocale => {
  if (!value) return "en";
  const supported: SupportedLocale[] = ["en", "zh", "ja", "ko", "th", "vi", "id", "ms", "es"];
  const prefix = value.toLowerCase().split('-')[0] as SupportedLocale;
  return supported.includes(prefix) ? prefix : "en";
};

type AppContentProps = Pick<AppProps, "Component" | "pageProps">;

function AppContent({ Component, pageProps }: AppContentProps) {
  const t = useTranslations("app");

  return (
    <>
      <Head>
        <link
          rel="preload"
          href="/fonts/AdelleSans-Regular.woff"
          as="font"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="/fonts/AdelleSans-Regular.woff2"
          as="font"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="/fonts/AdelleSans-Semibold.woff"
          as="font"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="/fonts/AdelleSans-Semibold.woff2"
          as="font"
          crossOrigin=""
        />

        <link rel="icon" href="/favicons/favicon.ico" sizes="any" />
        <link rel="icon" href="/favicons/icon.svg" type="image/svg+xml" />
        <link rel="apple-touch-icon" href="/favicons/apple-touch-icon.png" />
        <link rel="manifest" href="/favicons/manifest.json" />

        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no" />
        <title>{t("title")}</title>
        <meta name="description" content={t("description")} />
      </Head>

      <Script src="https://www.googletagmanager.com/gtag/js?id=G-V5RLQX9XRV" strategy="afterInteractive" />
      <Script id="gtag-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-V5RLQX9XRV', { page_path: window.location.pathname });
        `}
      </Script>

      <Component {...pageProps} />
    </>
  );
}

function MyApp({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const [locale, setLocale] = useState<SupportedLocale>("en");

  useEffect(() => {
    if (!router.isReady) return;

    const queryValue = router.query.lang;
    const langParam = Array.isArray(queryValue) ? queryValue[0] : queryValue;
    if (langParam) {
      const normalized = normalizeLocale(langParam);
      setLocale(normalized);
      if (typeof window !== "undefined") {
        localStorage.setItem(LANG_STORAGE_KEY, normalized);
      }
      return;
    }

    if (typeof window === "undefined") return;
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    const normalizedStored = normalizeLocale(stored || undefined);
    setLocale(normalizedStored);
    if (!stored) {
      localStorage.setItem(LANG_STORAGE_KEY, normalizedStored);
    }
  }, [router.isReady, router.query.lang]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      const newLocale = normalizeLocale(customEvent.detail);
      setLocale(newLocale);
      if (typeof window !== "undefined") {
        localStorage.setItem(LANG_STORAGE_KEY, newLocale);
      }
    };

    window.addEventListener('playsout-language-change', handleLanguageChange);
    return () => {
      window.removeEventListener('playsout-language-change', handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      // @ts-ignore
      window.gtag?.("config", "G-V5RLQX9XRV", { page_path: url });
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const targetOrigin = "*";
    let lastUrl = "";

    const resolveUrl = (url?: string) => {
      if (!url) return window.location.href;
      try {
        return new URL(url, window.location.origin).href;
      } catch {
        return window.location.href;
      }
    };

    const notifyUrlChange = (source: string, url?: string) => {
      const nextUrl = resolveUrl(url);
      if (nextUrl === lastUrl) return;
      lastUrl = nextUrl;

      window.parent?.postMessage(
        {
          type: "iframe:url-change",
          url: nextUrl,
        },
        targetOrigin
      );
      console.debug("[Playsout] iframe:url-change", { url: nextUrl, source });
    };

    const handleRouteChange = (url: string) => {
      notifyUrlChange("next:routeChangeComplete", url);
    };

    router.events.on("routeChangeComplete", handleRouteChange);

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      const result = originalPushState.apply(this, args as any);
      notifyUrlChange("history:pushState");
      return result;
    } as History["pushState"];

    window.history.replaceState = function (...args) {
      const result = originalReplaceState.apply(this, args as any);
      notifyUrlChange("history:replaceState");
      return result;
    } as History["replaceState"];

    const handlePopState = () => {
      notifyUrlChange("history:popstate");
    };

    window.addEventListener("popstate", handlePopState);
    notifyUrlChange("initial");

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", handlePopState);
    };
  }, [router.events]);

  const messagesMap: Record<SupportedLocale, any> = {
    en: enMessages,
    zh: zhMessages,
    ja: jaMessages,
    ko: koMessages,
    th: thMessages,
    vi: viMessages,
    id: idMessages,
    ms: msMessages,
    es: esMessages,
  };

  const messages = messagesMap[locale] || enMessages;

  return (
    <IntlProvider locale={locale} messages={messages} timeZone="UTC">
      <AppContent Component={Component} pageProps={pageProps} />
    </IntlProvider>
  );
}

export default MyApp;
